package com.sweetlime.bingeamigo.presentation.features.profile

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.sweetlime.bingeamigo.model.UserActivity
import com.sweetlime.bingeamigo.model.UserProfile
import com.sweetlime.bingeamigo.repository.AuthRepository
import com.sweetlime.bingeamigo.repository.UserRepository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

class ProfileViewModel(
    private val repository: UserRepository,
    private val authRepository: AuthRepository
) : ViewModel() {

    private val _profile = MutableStateFlow<UserProfile?>(null)
    val profile = _profile.asStateFlow()

    // Email is intentionally not stored in the public user document; source it
    // from the authenticated session so it stays private to the owner.
    val userEmail: String = authRepository.currentUserEmail() ?: ""

    private val _followers = MutableStateFlow<List<UserProfile>>(emptyList())
    val followers = _followers.asStateFlow()

    private val _following = MutableStateFlow<List<UserProfile>>(emptyList())
    val following = _following.asStateFlow()

    private val _activity = MutableStateFlow<List<UserActivity>>(emptyList())
    val activity = _activity.asStateFlow()

    private val _isSavingUsername = MutableStateFlow(false)
    val isSavingUsername = _isSavingUsername.asStateFlow()

    private val _usernameError = MutableStateFlow<String?>(null)
    val usernameError = _usernameError.asStateFlow()

    private val _isSavingAvatar = MutableStateFlow(false)
    val isSavingAvatar = _isSavingAvatar.asStateFlow()

    private val _isDeletingAccount = MutableStateFlow(false)
    val isDeletingAccount = _isDeletingAccount.asStateFlow()

    private val _deleteAccountError = MutableStateFlow<String?>(null)
    val deleteAccountError = _deleteAccountError.asStateFlow()

    fun loadProfile() {
        val uid = authRepository.currentUserId() ?: return
        if (_profile.value?.id == uid) return
        // Different user — clear all cached data before reloading
        _profile.value = null
        _followers.value = emptyList()
        _following.value = emptyList()
        _activity.value = emptyList()
        viewModelScope.launch {
            _profile.value = repository.getUserProfile(uid)
        }
    }

    fun loadFollowers() {
        if (_followers.value.isNotEmpty()) return
        viewModelScope.launch {
            val uid = authRepository.currentUserId() ?: return@launch
            _followers.value = repository.getFollowers(uid)
        }
    }

    fun loadFollowing() {
        if (_following.value.isNotEmpty()) return
        viewModelScope.launch {
            val uid = authRepository.currentUserId() ?: return@launch
            _following.value = repository.getFollowing(uid)
        }
    }

    fun loadActivity() {
        if (_activity.value.isNotEmpty()) return
        viewModelScope.launch {
            val uid = authRepository.currentUserId() ?: return@launch
            _activity.value = repository.getUserActivity(uid)
                .sortedByDescending { it.timestamp }
                .take(5)
        }
    }

    fun updateAvatar(avatarUrl: String, onSuccess: () -> Unit) {
        viewModelScope.launch {
            val uid = authRepository.currentUserId() ?: return@launch
            _isSavingAvatar.value = true
            try {
                repository.updateAvatar(uid, avatarUrl)
                _profile.value = _profile.value?.copy(avatarUrl = avatarUrl)
                onSuccess()
            } catch (e: Exception) {
                println(e)
            } finally {
                _isSavingAvatar.value = false
            }
        }
    }

    fun deleteAccount(onSuccess: () -> Unit) {
        viewModelScope.launch {
            authRepository.currentUserId() ?: return@launch
            _isDeletingAccount.value = true
            _deleteAccountError.value = null
            try {
                // The Cloud Function deletes all Firestore data and the auth account.
                authRepository.deleteAccount()
                onSuccess()
            } catch (e: Exception) {
                val msg = e.message ?: ""
                _deleteAccountError.value = if (
                    msg.contains("requires-recent-login", ignoreCase = true) ||
                    msg.contains("recent", ignoreCase = true) ||
                    msg.contains("reauthenticate", ignoreCase = true)
                ) {
                    "Your session has expired. Please log out and log back in before deleting your account."
                } else {
                    "Failed to delete account. Please try again."
                }
            } finally {
                _isDeletingAccount.value = false
            }
        }
    }

    fun clearDeleteAccountError() {
        _deleteAccountError.value = null
    }

    fun updateUsername(newUsername: String, onSuccess: () -> Unit) {
        val trimmed = newUsername.trim()
        if (trimmed.isBlank()) {
            _usernameError.value = "Username cannot be empty"
            return
        }
        viewModelScope.launch {
            val uid = authRepository.currentUserId() ?: return@launch
            _isSavingUsername.value = true
            _usernameError.value = null
            try {
                repository.updateUsername(uid, trimmed)
                _profile.value = _profile.value?.copy(username = trimmed)
                onSuccess()
            } catch (e: Exception) {
                println(e)
                _usernameError.value = "Failed to update username"
            } finally {
                _isSavingUsername.value = false
            }
        }
    }
}